import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { can, type Capability, type UserRole } from '@jalupro/shared';
import { useAuth } from './AuthContext';
import { homePathForRole } from './roles';
import { useT } from '../i18n';
import dict from './guards.i18n';

/**
 * Route guards (used as element wrappers around route subtrees in App.tsx).
 *
 * - <RequireAuth roles={[...]}> — must be signed in; optionally restricted to roles.
 *   Unauthenticated → /login (preserving the intended location). Wrong role →
 *   bounced to that role's home (no cross-portal leakage).
 * - <RequireRole roles={[...]}> — for fine-grained gating INSIDE the admin area
 *   (e.g. SUPER_ADMIN-only Users/Settings). Wrong role renders a Forbidden notice.
 */

function FullScreenMessage({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="cl col center"
      style={{ height: '100%', justifyContent: 'center', alignItems: 'center', gap: 8 }}
    >
      {children}
    </div>
  );
}

export interface RequireAuthProps {
  /** When set, only these roles may enter; others are redirected to their home. */
  roles?: readonly UserRole[];
}

export function RequireAuth({ roles }: RequireAuthProps) {
  const { isAuthenticated, role, initializing } = useAuth();
  const location = useLocation();
  const t = useT(dict);

  if (initializing) {
    return (
      <FullScreenMessage>
        <div className="meta">{t('loading')}</div>
      </FullScreenMessage>
    );
  }

  if (!isAuthenticated || !role) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (roles && !roles.includes(role)) {
    // Authenticated but not allowed in this portal — send to their own home.
    return <Navigate to={homePathForRole(role)} replace />;
  }

  return <Outlet />;
}

export interface RequireRoleProps {
  /** Explicit role allow-list (legacy). Prefer `cap`. */
  roles?: readonly UserRole[];
  /** Capability the role must hold in the EFFECTIVE permission matrix (evaluated per render). */
  cap?: Capability;
  children: React.ReactNode;
}

/** In-area role gate: renders children only if the user's role is allowed. */
export function RequireRole({ roles, cap, children }: RequireRoleProps) {
  const { role } = useAuth();
  const t = useT(dict);
  const allowed = !!role && (cap ? can(role, cap) : !!roles && roles.includes(role));
  if (!allowed) {
    return (
      <FullScreenMessage>
        <div className="h2">{t('notAvailable')}</div>
        <div className="meta">{t('noAccess')}</div>
      </FullScreenMessage>
    );
  }
  return <>{children}</>;
}
